import { z } from 'zod';
import { confirmationRecipientRoleSchema, confirmationRecipientSchema, } from '../inquiry/contacts.js';
import { uuidV7Schema } from '../uuid.js';
import { appointmentDurationSchema } from './appointment-duration.js';
/**
 * `Appointment` — le rendez-vous pris avec l'usager pour un contrôle.
 *
 * ⚠️ **Il ne porte pas d'état**, et c'est M3 : le seul `status` du modèle est
 * `Inquiry.status`. Les quatre prédicats d'écran que le prototype appelle `aPlanifier`,
 * `planifie`, `rdvFait` et `sansRDV` se **calculent** depuis la présence d'un rendez-vous et sa
 * date ; les stocker imposerait de les tenir cohérents à chaque écriture, et ça se
 * désynchronise toujours.
 *
 * ⚠️ Sous-ensemble : `result`, `reason` et `comment` du diagramme n'entrent **pas** ici. Ils
 * décrivent ce qu'est *devenu* un rendez-vous — tenu, annulé, reporté — donc le report et
 * l'annulation, qui n'ont pas d'écran à ce jour. Même règle que `Site` en S4 : une colonne entre
 * avec ce qui la lit. Ce qui manque n'est pas oublié, il est nommé ici.
 *
 * ⚠️ Le diagramme nommait cette colonne `date`. Elle s'appelle `scheduled_at` : `date` est un
 * nom de type SQL, et le dépôt suffixe déjà `_at` tout horodatage (`created_at`, `expires_at`,
 * `revoked_at`). Le diagramme a été mis à jour, il ne reste pas faux.
 */
export const appointmentSchema = z.object({
    id: uuidV7Schema,
    inquiryId: uuidV7Schema,
    /**
     * Instant du rendez-vous, avec fuseau (M5).
     *
     * ⚠️ Le fuseau n'est pas décoratif : la confirmation envoyée à l'usager affiche une heure
     * locale, et un `timestamp` nu perdrait l'information dont dépend l'heure affichée — sans
     * qu'aucune migration puisse ensuite dire de quel fuseau venait la valeur stockée.
     */
    scheduledAt: z.date(),
});
/**
 * Pourquoi un rendez-vous est **déplacé** — les quatre motifs du modèle (M24), ceux que la frame
 * Terrain 2.1 propose sous « Motif de la modification ». Le bureau les lit dans le fil d'activité.
 */
export const APPOINTMENT_REASONS = [
    'reorganization',
    'unavailable',
    'refused',
    'client_notified_absence',
];
export const appointmentReasonSchema = z.enum(APPOINTMENT_REASONS);
/** Verbatim de la frame Terrain 2.1. */
export const APPOINTMENT_REASON_LABELS = {
    reorganization: 'Réorganisation de tournée',
    unavailable: 'Indisponible',
    refused: 'Refus',
    client_notified_absence: 'Absence',
};
/**
 * Les deux phrases à cocher de la confirmation (#570) — les cases de la macro Excel de GHP, dans
 * l'ordre où le mail les écrit. Chacune demande une réponse **par mail** : elles ne s'offrent que
 * si un e-mail part.
 */
export const CONFIRMATION_PHRASES = ['water_supply', 'attendee_details'];
export const confirmationPhraseSchema = z.enum(CONFIRMATION_PHRASES);
/** Les libellés des cases, verbatim de la macro (frame Back Office 1.9b). */
export const CONFIRMATION_PHRASE_LABELS = {
    water_supply: 'Merci de confirmer si l’eau en service',
    attendee_details: 'Merci de confirmer Nom personne présente',
};
/**
 * Ce qu'un opérateur fournit pour poser un rendez-vous. L'`id` n'y est pas : il est tiré côté
 * serveur (M7), et laisser le client le choisir ouvrirait l'écriture sur un rendez-vous
 * existant.
 *
 * ⚠️ **Le format est exigé avant la coercition, et ce n'est pas de la ceinture-bretelles.**
 * `z.coerce.date()` seul appelle `new Date(valeur)` sur ce qu'on lui donne : `null`, `false` et
 * `0` deviennent alors le 1ᵉʳ janvier 1970 sans lever. Un champ date resté vide côté front
 * répondrait `201` et confirmerait à l'usager un rendez-vous en 1970. `z.iso.datetime()` exige
 * d'abord une chaîne ISO — ce que le JSON transporte réellement —, la coercition ne fait plus que
 * la convertir.
 *
 * ⚠️ `offset: true` accepte aussi bien `…Z` que `…+02:00` : les deux portent leur fuseau (M5).
 * Un horodatage sans fuseau, lui, reste refusé — c'est précisément l'information dont dépend
 * l'heure affichée dans la confirmation.
 */
export const scheduleAppointmentSchema = z.object({
    inquiryId: uuidV7Schema,
    scheduledAt: z.iso.datetime({ offset: true }).pipe(z.coerce.date()),
    /**
     * La durée, en minutes (#431). Absente, le rendez-vous garde celle de celui qu'il remplace, et
     * prend celle de son type de contrôle s'il n'y en avait pas (`appointmentDefaultDuration`, #578).
     */
    durationMinutes: appointmentDurationSchema.optional(),
    /**
     * À qui part la confirmation (#407). Absent : la personne présente, selon
     * `confirmationRecipientOf`. `'applicant'` : le bureau a choisi de prévenir le demandeur, parce
     * que la personne présente n'a pas d'e-mail — un choix qu'il fait à l'écran, jamais un repli.
     */
    confirmationRecipient: confirmationRecipientRoleSchema.optional(),
    /**
     * Les phrases que le bureau a cochées (#570) : chacune ajoute sa demande au mail qui part. Absent
     * ou vide, aucune. Elles ne s'enregistrent pas sur le rendez-vous : elles ne servent qu'au mail.
     */
    confirmationPhrases: z.array(confirmationPhraseSchema).optional(),
    /**
     * « Envoyer la confirmation par SMS » (#572) : cochée quand la personne présente n'a pas d'e-mail
     * mais un mobile. Absente ou fausse, aucun SMS ne part — c'est le « ou pas » de GHP.
     */
    smsConfirmation: z.boolean().optional(),
});
/**
 * Ce qu'il faut pour **annuler** le rendez-vous d'une demande : la demande, et rien d'autre.
 *
 * ⚠️ **L'annulation porte sur la demande, pas sur un rendez-vous précis**, et c'est le prototype
 * (`data-dm-unplan`, `mocks/ghp-bo/app.js:317-322`, qui pose `d.rdv = null`). Une demande peut en
 * porter plusieurs — un report en crée un second (M24) —, et annuler le dernier ferait
 * **réapparaître l'avant-dernier** comme rendez-vous courant : l'agent se déplacerait à une date
 * que personne n'a reconfirmée. « Cette demande n'a plus de rendez-vous » est le seul état que
 * l'usager qui se décommande demande.
 *
 * ⚠️ **Aucun motif n'est demandé.** Le prototype n'en demande pas, et rien n'a de colonne où le
 * ranger : un champ libre qui n'irait nulle part vaut moins que son absence.
 */
export const cancelAppointmentsSchema = z.object({
    inquiryId: uuidV7Schema,
    /**
     * « Prévenir l'usager par e-mail » (#573) : l'annulation du créneau part à la personne du
     * rendez-vous. **Faux par défaut** : l'annulation vient le plus souvent de l'usager lui-même, et
     * lui écrire qu'il s'est décommandé n'apprend rien.
     */
    notify: z.boolean().default(false),
});
/**
 * L'**annulation d'un créneau envoyée à l'usager** (#573) : le créneau annulé, tel qu'il lui avait
 * été annoncé, et à qui le mail part — ou pourquoi il ne part pas.
 *
 * ⚠️ **`queued: false` n'est pas un échec**, comme pour la confirmation : le créneau est annulé,
 * mais personne n'était joignable, ou la mise en file a échoué. L'écran le dit (M21).
 */
export const appointmentCancellationSchema = z.object({
    scheduledAt: z.iso.datetime({ offset: true }),
    queued: z.boolean(),
    recipient: confirmationRecipientSchema,
});
/**
 * Le SMS de confirmation (#572) : le téléphone tel qu'il est noté, le nom de la personne, et s'il
 * est parti en file. `queued: false` n'est pas un échec du rendez-vous, comme pour l'e-mail : pas
 * de mobile, ou la mise en file a échoué.
 */
export const smsConfirmationSchema = z.object({
    phone: z.string().nullable(),
    name: z.string().nullable(),
    queued: z.boolean(),
});
/**
 * Ce que l'annulation rend : **combien de rendez-vous elle a retirés**.
 *
 * ⚠️ **Zéro n'est pas une erreur**, c'est une information : la demande n'en avait aucun — deux
 * onglets ouverts, ou un double clic. L'écran le dit plutôt que de faire croire à une annulation
 * qui n'a rien annulé (M21).
 */
export const cancelledAppointmentsSchema = z.object({
    inquiryId: uuidV7Schema,
    cancelled: z.number().int().min(0),
    /** L'annulation envoyée à l'usager quand le bureau l'a demandé, sinon `null` ; absente d'une API d'avant. */
    cancellation: appointmentCancellationSchema.nullish(),
});
/**
 * Ce que `POST /planning/appointments` rend : le rendez-vous écrit, **et si la confirmation est
 * partie**.
 *
 * ⚠️ **`confirmationQueued: false` n'est pas un échec**, et l'interface doit le montrer plutôt
 * que de le taire (M21, M24) : le rendez-vous est pris, mais personne n'était joignable — le
 * demandeur manque, ou son adresse est inexploitable. Un opérateur qui croit l'usager prévenu
 * alors qu'il ne l'est pas ne le rappellera jamais. C'est la seule raison pour laquelle cette
 * enveloppe existe au lieu de rendre le rendez-vous seul.
 *
 * ⚠️ **`scheduledAt` est ici une chaîne ISO, alors qu'`appointmentSchema` exige une `Date`** — et
 * ce n'est pas une incohérence. Celui-là décrit la **ligne en base**, où la colonne est un
 * `timestamptz` ; celui-ci décrit ce qui passe **sur le fil**, où `JSON.stringify` d'une `Date`
 * rend une chaîne. Le client d'API valide la réponse contre ce schéma (D17) : lui donner
 * `z.date()` ferait échouer toute réponse pourtant correcte. Même forme que `lastAppointmentAt`
 * sur une ligne de relance.
 *
 * `offset: true` accepte `…Z` comme `…+02:00` : les deux portent leur fuseau (M5).
 */
export const scheduledAppointmentSchema = z.object({
    appointment: appointmentSchema.extend({
        scheduledAt: z.iso.datetime({ offset: true }),
    }),
    confirmationQueued: z.boolean(),
    /**
     * À qui la confirmation est partie, ou pourquoi elle n'est pas partie (#407) — le compte rendu
     * le dit même quand l'écran ne l'a pas annoncé, après un dépôt sur le planning. `null` quand
     * aucune n'a été tentée (le même créneau reposé) ; absent d'une API d'avant.
     */
    recipient: confirmationRecipientSchema.nullish(),
    /**
     * Après un **déplacement** (#573) : l'annulation de l'ancien créneau, partie avant la confirmation
     * du nouveau, au même destinataire. `null` sans créneau remplacé — une première pose, ou le même
     * créneau reposé — ; absente d'une API d'avant.
     */
    cancellation: appointmentCancellationSchema.nullish(),
    /**
     * La confirmation **par SMS** (#572), quand le bureau l'a cochée : au téléphone de qui, et si
     * elle est partie en file. `null` sans case cochée ; absente d'une API d'avant.
     */
    sms: smsConfirmationSchema.nullish(),
});
/**
 * Le fuseau dans lequel un rendez-vous se lit. **Nommé, jamais un décalage figé** : `+02:00`
 * serait faux six mois par an, et un rendez-vous de janvier s'afficherait une heure trop tard.
 *
 * ⚠️ `deadline.ts` porte sa propre constante, de même valeur, et ce n'est pas un doublon à
 * fusionner : celle-là dit dans quel fuseau **une échéance se calcule**, celle-ci dans quel fuseau
 * **une heure s'affiche**. Les deux pourraient diverger — une échéance réglementaire n'a pas à
 * suivre le fuseau d'affichage — et les confondre ferait croire l'inverse.
 */
const TIME_ZONE = 'Europe/Paris';
const DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
});
const NAMED_DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', {
    timeZone: TIME_ZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
});
const SHORT_DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
});
const TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
});
/**
 * Le créneau d'un rendez-vous, **mis en forme une seule fois pour les trois apps**.
 *
 * ⚠️ **Ici et pas dans chaque écran**, exactement pour la raison qui a fait naître `displayName`
 * (M24 § 2) : le back-office, la tablette et la confirmation e-mail annoncent le *même* rendez-vous
 * à la même personne. Trois mises en forme, c'est trois occasions d'afficher trois heures — et
 * celle que l'usager a entendue au téléphone n'en est plus qu'une sur trois.
 *
 * ⚠️ **Le fuseau est imposé, pas emprunté à la machine.** `toLocaleString()` sans `timeZone` suit
 * le réglage du navigateur : une tablette mal réglée, ou un poste en UTC, afficherait une heure que
 * personne n'a annoncée à l'usager. C'est la même raison qui fait que la confirmation e-mail pose
 * `Europe/Paris` explicitement côté serveur — le conteneur, lui, tourne en UTC.
 *
 * L'entrée est la **chaîne ISO du contrat** (`assignedInquirySchema`, `scheduledAppointmentSchema`),
 * pas une `Date` : c'est ce que les écrans reçoivent et ce que la tablette range dans son cache.
 *
 * ⚠️ **`null` d'une chaîne illisible** : la tablette relit un cache que rien ne revalide, et
 * `format` lèverait en plein rendu React.
 */
export function appointmentSlot(scheduledAt) {
    const instant = new Date(scheduledAt);
    if (Number.isNaN(instant.getTime()))
        return null;
    return {
        day: DAY_FORMAT.format(instant),
        time: TIME_FORMAT.format(instant),
        namedDay: NAMED_DAY_FORMAT.format(instant),
        shortDay: SHORT_DAY_FORMAT.format(instant),
    };
}
/**
 * Le décalage de Paris, en minutes, pour un instant donné — lu au fuseau plutôt que codé en dur,
 * parce qu'il vaut +60 ou +120 selon la saison.
 */
function parisOffsetMinutes(at) {
    const named = new Intl.DateTimeFormat('en-US', {
        timeZone: TIME_ZONE,
        timeZoneName: 'longOffset',
    })
        .formatToParts(at)
        .find((part) => part.type === 'timeZoneName')?.value;
    const parsed = /^GMT([+-])(\d{2}):(\d{2})$/u.exec(named ?? '');
    // `GMT` tout court est ce que renvoie le format pour un décalage nul : ce n'est pas un échec.
    if (!parsed)
        return 0;
    const [, sign, hours, minutes] = parsed;
    return (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes));
}
/**
 * Le créneau saisi par un opérateur, converti en instant absolu — **l'heure tapée est une heure
 * de Paris**, quelle que soit la machine qui la saisit.
 *
 * ⚠️ **C'est tout l'objet de cette fonction, et elle existe pour empêcher un défaut silencieux.**
 * `new Date('2026-08-04T10:00')` lit l'heure au fuseau du **navigateur** : sur un poste réglé en
 * UTC — cas banal d'une machine de bureau mal configurée ou d'un conteneur — un « 10:00 » tapé
 * part à 10:00 UTC, l'usager s'entend annoncer 12:00, et rien n'échoue nulle part. Le rendez-vous
 * est alors faux pour tout le monde : la confirmation, la tournée de l'agent, et la personne qui
 * attend chez elle.
 *
 * ⚠️ **Le décalage est lu deux fois, et ce n'est pas de la superstition.** Le premier passage lit
 * le décalage à l'instant *approché* (l'heure murale prise pour de l'UTC) ; près d'une bascule
 * d'heure d'été, cet instant peut tomber du mauvais côté et rendre +60 au lieu de +120. Le second
 * passage relit le décalage à l'instant corrigé et tranche. C'est la méthode standard, et le test
 * qui l'épingle porte sur les deux jours qui encadrent le changement d'heure.
 *
 * `day` est un jour ISO (`2026-08-04`) et `time` une heure murale (`10:00`) — exactement ce que
 * rendent les `<input type="date">` et `<input type="time">` du navigateur.
 *
 * ⚠️ **`null` quand le couple ne désigne aucun instant**, et ce n'est pas défensif pour rien : un
 * `<input type="date">` accepte **quatre chiffres ou plus** pour l'année — Chrome en prend six —,
 * donc une frappe en trop rend `20266-08-04`, non vide et pourtant sans instant. La fonction
 * rendait alors `Invalid Date` à `formatToParts`, qui **lève** ; l'exception partait d'un
 * `onClick`, qu'aucune frontière React ne rattrape, et le bouton restait muet. Le refus se lit
 * désormais dans le type : l'appelant ne peut plus l'ignorer (M21 — on signale, on ne se taît pas).
 *
 * ⚠️ **La validité se vérifie par aller-retour, pas par `Number.isNaN`**, et la nuance est celle
 * qui compte : `new Date('2026-02-31T10:00:00.000Z')` ne rend **pas** `Invalid Date`, il **décale**
 * au 3 mars. Un jour impossible deviendrait un rendez-vous parfaitement valide à une autre date —
 * le défaut muet que toute cette fonction existe pour empêcher, sous une autre forme. N'est donc
 * accepté qu'un couple que le parseur rend **à l'identique**.
 */
export function parisInstant(day, time) {
    const wallClock = `${day}T${time}:00.000Z`;
    const wallClockAsUtc = new Date(wallClock);
    if (Number.isNaN(wallClockAsUtc.getTime()))
        return null;
    if (wallClockAsUtc.toISOString() !== wallClock)
        return null;
    const approached = new Date(wallClockAsUtc.getTime() - parisOffsetMinutes(wallClockAsUtc) * 60_000);
    return new Date(wallClockAsUtc.getTime() - parisOffsetMinutes(approached) * 60_000).toISOString();
}
/**
 * Les deux champs de saisie d'un rendez-vous **déjà posé** — l'inverse exact de `parisInstant`.
 *
 * ⚠️ **Elle existe pour que rouvrir une boîte n'y change pas l'heure.** Le back-office ouvre la
 * gestion d'une demande sur son créneau en place ; sans cette conversion, l'écran retomberait sur
 * `toISOString().slice(…)`, qui lit **UTC** — un rendez-vous de 09:00 rouvrirait à 07:00 l'été,
 * et « Enregistrer » sans toucher aux champs l'avancerait de deux heures. Le défaut est muet : la
 * saisie paraît juste, et c'est le second enregistrement qui déplace l'usager.
 *
 * ⚠️ **`formatToParts`, pas un format de chaîne** : `Intl` n'a aucun motif qui rende `YYYY-MM-DD`
 * de façon garantie dans toutes les locales, et composer sur `fr-FR` rendrait `04/08/2026` — que
 * `<input type="date">` refuse en silence, champ vide à l'écran. Les parties se lisent nommées,
 * puis se recomposent.
 *
 * Le pendant en lecture seule est `appointmentSlot`, qui rend ce qu'un écran **affiche**
 * (`04/08/2026`, `08:30`) ; celle-ci rend ce qu'un `<input>` **accepte**.
 */
export function appointmentFields(scheduledAt) {
    const instant = new Date(scheduledAt);
    if (Number.isNaN(instant.getTime()))
        return null;
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(instant);
    const part = (type) => parts.find((candidate) => candidate.type === type)?.value ?? '';
    // `hour12: false` rend `24` pour minuit sur certaines plateformes — une heure que le champ
    // refuse. Minuit s'écrit `00`, et c'est le même instant.
    const hour = part('hour') === '24' ? '00' : part('hour');
    return {
        day: `${part('year')}-${part('month')}-${part('day')}`,
        time: `${hour}:${part('minute')}`,
    };
}
//# sourceMappingURL=appointment.js.map